import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import '../styles/DocsUpload.css';

// Set up axios defaults
axios.defaults.withCredentials = true;
axios.defaults.headers.common['x-api-key'] = process.env.REACT_APP_CREATE_USER_API_KEY;

// Create an axios instance for file uploads
const fileUploadAxios = axios.create({
  withCredentials: true,
  headers: {
    'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
    'Accept': 'multipart/form-data'  // Add this line
  }
});

const VolvoDocsUpload = () => {
  const [formData, setFormData] = useState({
    title: '',
    authors: '',
    document_type: 'strategy',
    business_area: 'marketing_strategy',
    sub_categories: '',
    confidentiality_level: 'internal'
  });
  
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null, success: false, processing: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: null, success: false, processing: false });
    
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach((key) => submitData.append(key, formData[key]));
      
      if (file) {
        submitData.append('fileInput', file);
      }

      const response = await fileUploadAxios.post(
        'https://n8n.iniai.cloud/webhook/volvo-documents-upload', 
        submitData
      );

      setStatus({ 
        loading: false, 
        error: null, 
        success: true,
        processing: true 
      });

      setFormData({
        title: '',
        authors: '',
        document_type: 'strategy',
        business_area: 'marketing_strategy',
        sub_categories: '',
        confidentiality_level: 'internal'
      });
      setFile(null);
      
    } catch (error) {
      console.error('Upload error details:', error.response || error);
      setStatus({ 
        loading: false, 
        error: error.response?.data?.message || error.message || 'Upload failed', 
        success: false,
        processing: false
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      const selectedFile = e.target.files[0];
      console.log('Selected file size:', selectedFile.size / 1024 / 1024, 'MB');
      
      if (selectedFile.size > 45 * 1024 * 1024) { // 45MB limit
        setStatus({
          loading: false,
          error: 'File size must be less than 45MB',
          success: false,
          processing: false
        });
        return;
      }
      setFile(selectedFile);
    }
  };

  return (
    <div className="form-container">
      <h2 className="form-title">Volvo Documents Upload</h2>
      <p className="form-subtitle">Submit your document upload request</p>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Document Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="form-input"
            placeholder="Enter document title"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Authors</label>
          <input
            type="text"
            name="authors"
            value={formData.authors}
            onChange={handleChange}
            required
            className="form-input"
            placeholder="Enter authors"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Document Type</label>
          <select
            name="document_type"
            value={formData.document_type}
            onChange={handleChange}
            className="form-select"
          >
            <option value="strategy">Strategy</option>
            <option value="framework">Framework</option>
            <option value="guidelines">Guidelines</option>
            <option value="case_study">Case Study</option>
            <option value="report">Report</option>
            <option value="training">Training</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Business Area</label>
          <select
            name="business_area"
            value={formData.business_area}
            onChange={handleChange}
            className="form-select"
          >
            <option value="strategy">Strategy</option>
            <option value="activation">Activation</option>
            <option value="planning">Planning</option>
            <option value="analytics">Analytics</option>
            <option value="ad_tech">Ad Tech</option>
            <option value="process_operations">Process and Operations</option>
            <option value="finance">Finance</option>
            <option value="trafficking">Content & Trafficking</option>
            <option value="rfp_materials">RFP Materials</option>
            <option value="news_knowledge">News and Knowledge</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Sub-categories</label>
          <input
            type="text"
            name="sub_categories"
            value={formData.sub_categories}
            onChange={handleChange}
            className="form-input"
            placeholder="Enter sub-categories (comma-separated)"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Confidentiality Level</label>
          <select
            name="confidentiality_level"
            value={formData.confidentiality_level}
            onChange={handleChange}
            className="form-select"
          >
            <option value="public">Public</option>
            <option value="internal">Internal</option>
            <option value="confidential">Confidential</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Document File</label>
          <input
            type="file"
            name="fileInput"
            onChange={handleFileChange}
            required
            className="form-input"
            accept=".pdf,.doc,.docx"
          />
        </div>

        {status.error && (
          <div className="form-error">
            {status.error}
          </div>
        )}

        {status.success && (
          <div className="form-success">
            {status.processing 
              ? 'Document uploaded successfully! Processing may take up to 20 minutes.'
              : 'Document uploaded and processed successfully!'}
          </div>
        )}

        <button
          type="submit"
          disabled={status.loading || !file}
          className="form-submit"
        >
          {status.loading ? 'Uploading...' : 'Upload Document'}
        </button>
      </form>
    </div>
  );
};

export default VolvoDocsUpload;